import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MailCheck, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
      toast.success("Reset link ready");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="forgot-password-page">
      <div className="p-6">
        <Link
          to="/login"
          data-testid="back-login-link"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
        >
          <ArrowLeft size={16} /> Back to Login
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md brutal-card p-8 md:p-10 bg-white">
          <div className="mb-6 flex items-center gap-3">
            <div className="p-3 border-2 border-[#0A0A0A] rounded-xl bg-[#FFE600]">
              <KeyRound size={22} strokeWidth={2.5} />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Password reset</span>
              <h1 className="font-heading font-black text-3xl mt-1 tracking-tighter">Forgot password?</h1>
            </div>
          </div>

          {!result ? (
            <>
              <p className="text-sm font-medium text-[#4A4A4A] mb-5">
                Enter your registered email. We'll generate a reset link valid for 30 minutes.
              </p>
              <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email</label>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] focus:ring-offset-2"
                    placeholder="you@example.com"
                  />
                </div>
                {error && (
                  <div
                    data-testid="forgot-error"
                    className="bg-[#FF4D4D] text-white border-2 border-[#0A0A0A] rounded-xl px-4 py-2 text-sm font-bold"
                  >
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
                >
                  {loading ? "Generating..." : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div className="space-y-4" data-testid="forgot-result">
              <div className="p-4 border-2 border-[#0A0A0A] rounded-xl bg-[#FFFCE0]">
                <div className="flex items-center gap-2 font-bold">
                  <MailCheck size={18} strokeWidth={2.5} />
                  <span>Check your email</span>
                </div>
                <p className="text-sm font-medium text-[#4A4A4A] mt-2">{result.message}</p>
                {result.note && (
                  <p className="text-xs font-bold text-[#FF4D4D] mt-3">{result.note}</p>
                )}
              </div>
              {result.reset_link && (
                <Link
                  to={result.reset_link.replace(/^https?:\/\/[^/]+/, "")}
                  data-testid="forgot-direct-link"
                  className="block w-full text-center font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
                >
                  Reset my password now →
                </Link>
              )}
              <Link
                to="/login"
                className="block w-full text-center font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-white"
              >
                Back to login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
